import * as THREE from 'three/webgpu';
import { attribute, positionGeometry, uniform } from 'three/tsl';

import { RENDER_ORDER, smokePuffMaterial, firePuffMaterial } from './materials.js';
import { makeFalloffTexture } from './ShadowBlobs.js';

/**
 * @file `VehicleFxBatch` — the smoke, the flame and the fireball of a damaged
 * car. TWO instanced draws for every burning vehicle in the city.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT OWNS THE GPU AND NOTHING ELSE
 * ─────────────────────────────────────────────────────────────────────────────
 * `systems/VehicleDamage.js` owns every particle's POSITION, RADIUS, ALPHA and
 * LIFETIME — it imports no three.js and is provable in node. This class knows
 * none of that. It is handed world-space numbers each frame and turns them into
 * two draw calls. There is no simulation here, no gravity, no ageing and no
 * clock: if a puff is not `add`ed this frame, it is not drawn this frame.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PER-FRAME REBUILD, NOT A POOL
 * ─────────────────────────────────────────────────────────────────────────────
 * `reset()` → `addSmoke()`/`addFire()`* → `commit()`, exactly like
 * `render/LightPools.js` and `render/MissionMarkers.js`, and for exactly their
 * reason: the particle set is a pure function of state the damage system
 * already holds, so re-deriving it is cheaper than keeping a free list honest
 * across a car despawning, exploding or being recycled by `Traffic` MID-FRAME.
 * THERE IS NO SLOT TO LEAK. A car that vanishes between two frames simply stops
 * being added, and nothing has to be told.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIVE THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **CAMERA-FACING, NOT GROUND-FLAT — and that is the one place this class
 *   departs from every other quad batch in the engine.** Blood splats, blob
 *   shadows, light pools and mission markers all lie in the XZ plane, because at
 *   the game's 52° pitch a ground-parallel quad reads correctly from every yaw
 *   and costs no camera data at all. That works because those things ARE on the
 *   ground. Smoke and fire RISE: a flat quad a metre above a bonnet is a
 *   foreshortened ellipse floating in mid air, and a plume built from them is a
 *   stack of pancakes. So the quads are billboarded — offset along a camera
 *   RIGHT and a camera UP vector in the vertex stage.
 *
 *   The basis arrives as two `uniform(Vector3)`s copied out of
 *   `camera.matrixWorld.elements` ({@link VehicleFxBatch.setCamera}), the same
 *   trick `render/weather.js` uses for the rain, and NOT a TSL camera-matrix
 *   accessor (whose spelling has moved between three releases) and NOT a
 *   `THREE.Sprite` (one object per puff) and NOT a per-instance CPU rotation
 *   (which would put the camera into the damage system's dependencies). Two
 *   `Vector3` writes a frame buy the whole effect.
 *
 * • **TWO LAYERS, TWO BLEND MODES, AND THEY ARE NOT INTERCHANGEABLE.** Smoke is
 *   ALPHA-blended and dark, because smoke OCCLUDES. Fire is ADDITIVE and over
 *   1.0, because fire is LIGHT. Swapping either is not a tuning difference: an
 *   additive plume is lit fog and an alpha-blended flame is an orange sticker.
 *
 * • **THE BLOOM BUDGET IS THE TUNING, not taste** — see {@link FX} for the
 *   arithmetic against `render/post.js`'s 0.80 (day) / 0.45 (night) thresholds,
 *   including the fact that additive fire SUMS with itself.
 *
 * • **POSITIONS ARE WORLD SPACE.** `positionNode` builds the vertex from `iPos`
 *   plus the camera basis and never sees the model matrix, so `parent` must be
 *   at IDENTITY (the engine scene or a level root). A rotated or translated
 *   parent silently shears the whole effect — same requirement, and the same
 *   failure mode, as `render/Billboards.js` and `render/weather.js`.
 *
 * • **Smoke puffs do not sort against each other.** `depthWrite` is off on both
 *   layers, so a pile of alpha-blended puffs composites in submission order
 *   rather than back-to-front. That is the standard trade for particle smoke
 *   (the rain makes it too); it is invisible on a soft radial falloff and the
 *   alternative is a per-frame depth sort of the whole buffer.
 *
 * @module engine/render/VehicleFx
 */

/** @typedef {import('../types.js').Registry} Registry */

/**
 * Tuning: LINEAR RGB, metres, 0..1 alphas. One frozen place, because every one
 * of these numbers was picked against a threshold rather than by eye.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BLOOM ARITHMETIC — `render/post.js` lerps the threshold 0.80 (day) → 0.45
 * (night). FIRE MUST CLEAR THE DAY NUMBER; SMOKE MUST NOT REACH THE NIGHT ONE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **Fire (ADDITIVE).** What lands in the framebuffer is
 * `tint * iAlpha * fireOpacity * mask`, ADDED to whatever was already there:
 *
 *   one puff, deep orange, mask 1: 1.30 * alpha  ⇒ over 0.80 from alpha ≥ 0.62
 *   one puff, yellow-white core:   1.55 * alpha  ⇒ over 0.80 from alpha ≥ 0.52
 *   at the {@link FX.fireAlphaMax} cap 0.85:     1.105 (cool) / 1.318 (hot) red
 *
 * so a SINGLE puff at anything like full strength glows in direct sunlight,
 * which is the requirement — a burning car that only reads at night is a
 * burning car nobody sees.
 *
 * ⚠ AND IT OVERLAPS ITSELF. Three puffs sharing a bonnet SUM to ~3.95 red /
 * ~3.21 green at the cap, i.e. the core saturates to white and the bloom halo is
 * several metres across. That IS the fireball, and it is the budget: the alpha
 * cap is the backstop that keeps a ten-puff explosion at a bounded ~13 rather
 * than an unbounded number, and it is why the cap exists at all. The real sum
 * is well under `n * peak` because the falloff mask is 1 only in a small core
 * and the puffs overlap mostly in their skirts.
 *
 * **Smoke (ALPHA-blended).** An alpha blend is a CONVEX COMBINATION, so the
 * result can never exceed the brighter of the puff and the pixel behind it —
 * smoke can only pull a pixel DOWN toward its own colour. The whole check is
 * therefore one number: the palest smoke, {@link FX.smokePale}, peaks at 0.40,
 * which is under the 0.45 night threshold with 0.05 to spare. A smoking car
 * cannot light up the street at midnight, and it cannot do so at ANY `alpha` —
 * the property is structural, not a tuning that a future emitter can break.
 * (Fog cannot break it either: `fog: true` lerps toward the fog colour, which is
 * ~0.73 linear by day — under the DAY threshold 0.80 — and near-black at night.)
 */
export const FX = Object.freeze({
  // ── the shared puff texture ────────────────────────────────────────────────
  /** Falloff resolution. 64 is the blob shadow's; a puff is a smear, not a line
   *  (contrast `MissionMarkers.js`'s 96 for a thin bright annulus). */
  puffSize: 64,
  /**
   * Normalised radius where the falloff starts, i.e. how big the solid core is.
   * 0.10 is nearly a pure gradient: a puff with a hard core reads as a BALL,
   * and a plume of balls reads as bubbles. `ShadowBlobs.js#makeFalloffTexture`
   * already takes this as a parameter, so there is no second generator here —
   * a second copy of a shared helper is a bug in this repo.
   */
  puffSoftness: 0.1,

  // ── smoke ──────────────────────────────────────────────────────────────────
  /** Global thickness on top of the per-instance alpha. Under an ALPHA blend
   *  this thins the puff rather than darkening it — the honest dial. */
  smokeOpacity: 0.55,
  /** `grey` 0 — the soot of a car that is properly on fire. Near-black. */
  smokeDark: /** @type {[number,number,number]} */ ([0.055, 0.052, 0.05]),
  /** `grey` 1 — the pale wisp of a car that is only smoking. THE 0.45 CHECK
   *  LIVES ON THIS TRIPLE: 0.40 peak, and slightly cool, because warm grey
   *  next to an orange flame reads as more flame. */
  smokePale: /** @type {[number,number,number]} */ ([0.38, 0.38, 0.4]),
  /** Ceiling on the per-instance alpha. 1 — smoke has no bloom budget to blow. */
  smokeAlphaMax: 1,

  // ── fire ───────────────────────────────────────────────────────────────────
  /** Global strength on top of the per-instance alpha. Interchangeable with
   *  scaling both tints (additive), kept separate so it is ONE number to tune. */
  fireOpacity: 1,
  /** `heat` 0 — the deep orange of the plume's cooling edge. Over 1 in red on
   *  purpose: `NoToneMapping` means values above 1 survive to the bloom pass. */
  fireCool: /** @type {[number,number,number]} */ ([1.3, 0.4, 0.06]),
  /** `heat` 1 — the yellow-white core. */
  fireHot: /** @type {[number,number,number]} */ ([1.55, 1.26, 0.8]),
  /** Ceiling on the per-instance alpha — the backstop on the additive sum. See
   *  the arithmetic above. */
  fireAlphaMax: 0.85,

  // ── shared clamps ──────────────────────────────────────────────────────────
  /** A puff smaller than this is not worth an instance; a puff bigger than this
   *  is a bug in the emitter, and a 40 m billboard fills the screen. */
  minRadius: 0.05,
  maxRadius: 8,
});

/**
 * The billboard quad: corners at ±1 so the instance's `iSize` IS the puff
 * RADIUS (the quad spans `2 * radius`), UVs 0..1 for the falloff lookup.
 *
 * Hand-built rather than a `PlaneGeometry` for the same reason `LightPools.js`
 * hand-builds its beam: the corner convention is load-bearing for the
 * `positionNode` below, and it should be readable in one place. Non-indexed,
 * two triangles, 6 vertices total whatever the instance count.
 */
const QUAD_POS = Object.freeze([
  -1, -1, 0, 1, -1, 0, 1, 1, 0,
  -1, -1, 0, 1, 1, 0, -1, 1, 0,
]);
const QUAD_UV = Object.freeze([0, 0, 1, 0, 1, 1, 0, 0, 1, 1, 0, 1]);

export class VehicleFxBatch {
  /**
   * @param {Object} o
   * @param {THREE.Object3D} o.parent Added here. MUST BE AT IDENTITY — the
   *   shader works in world space (see the module header).
   * @param {number} [o.maxSmoke=256] Smoke instance cap. Overflow is DROPPED and
   *   counted, never grown mid-frame.
   * @param {number} [o.maxFire=128] Fire instance cap. Half the smoke because a
   *   car smokes for its whole death and only burns at the end of it.
   * @param {Registry|null} [o.registry] Tracks the texture + both materials so
   *   `Level.dispose()` frees them.
   * @param {string} [o.name='vehicleFx']
   */
  constructor(o = {}) {
    const {
      parent = null,
      maxSmoke = 256,
      maxFire = 128,
      registry = null,
      name = 'vehicleFx',
    } = o;

    if (!(maxSmoke > 0)) throw new Error('VehicleFxBatch: `maxSmoke` must be > 0');
    if (!(maxFire > 0)) throw new Error('VehicleFxBatch: `maxFire` must be > 0');

    this._disposed = false;
    /** @type {THREE.Camera|null} */
    this._camera = null;

    // The billboard basis. Defaults to the WORLD axes so a batch that is never
    // handed a camera still draws upright quads instead of degenerate ones —
    // which is also what lets this class construct and commit in node.
    this._uRight = uniform(new THREE.Vector3(1, 0, 0));
    this._uUp = uniform(new THREE.Vector3(0, 1, 0));

    /**
     * ONE texture for BOTH layers, cached through the registry. Keyed by the
     * falloff's PARAMETERS rather than by this batch's `name`, so two callers
     * that want the same puff share one upload. (`ShadowBlobs.js` keys its own
     * on its instance name, so the blob's 0.45-softness texture is a separate
     * entry — a different image, correctly not shared.)
     * @type {THREE.Texture}
     */
    this.texture = registry
      ? registry.texture(
          `puff:${FX.puffSize}:${FX.puffSoftness}`,
          () => makePuffTexture(),
        )
      : makePuffTexture();

    const smokeMat = smokePuffMaterial({
      tex: this.texture,
      opacity: FX.smokeOpacity,
      name: `${name}:smoke`,
    });
    const fireMat = firePuffMaterial({
      tex: this.texture,
      opacity: FX.fireOpacity,
      name: `${name}:fire`,
    });
    for (const m of [smokeMat, fireMat]) {
      // The billboarding lives here, not in `materials.js`: the factories stay
      // camera-free (and node-constructible) and this is the only file that
      // knows the corner convention above.
      //
      //   world = iPos + right * (corner.x * radius) + up * (corner.y * radius)
      //
      // Both offsets are VERTEX-stage and constant across the quad's own
      // interpolation, so nothing here goes near the per-instance-integer trap
      // (`docs/rendering.md`): there is no integer, and no `floor`.
      const c = positionGeometry;
      const size = attribute('iSize', 'float');
      m.positionNode = attribute('iPos', 'vec3')
        .add(this._uRight.mul(c.x.mul(size)))
        .add(this._uUp.mul(c.y.mul(size)));
      registry?.track?.(m);
    }

    this._smoke = new PuffLayer(maxSmoke, smokeMat, `${name}:smoke`, RENDER_ORDER.smoke);
    this._fire = new PuffLayer(maxFire, fireMat, `${name}:fire`, RENDER_ORDER.fire);

    /** @type {THREE.Group} The pair of draws. Already parented. */
    this.object = new THREE.Group();
    this.object.name = name;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();
    this.object.add(this._smoke.object, this._fire.object);
    parent?.add(this.object);
  }

  /**
   * Feeds the billboard basis from a camera's world matrix, and REMEMBERS the
   * camera so {@link commit} can refresh it every frame — a batch set up once
   * at level build then stays correct as the camera moves. Pass `null` to
   * detach (the basis freezes at whatever it last was).
   *
   * ⚠ Reads `camera.matrixWorld`, so the caller must have updated it. The
   * engine's render loop already has by the time gameplay runs; `weather.js`
   * carries the same requirement.
   *
   * @param {THREE.Camera|null} camera
   * @returns {this}
   */
  setCamera(camera) {
    this._camera = camera ?? null;
    this._syncBasis();
    return this;
  }

  /** Drops last frame's particles. Call once per frame, before the `add`s. */
  reset() {
    this._smoke.reset();
    this._fire.reset();
    return this;
  }

  /**
   * One smoke puff.
   *
   * @param {number} x @param {number} y @param {number} z WORLD position of the
   *   puff's CENTRE (not its base — a billboard has no base).
   * @param {number} radius Metres. The quad is `2 * radius` across.
   * @param {number} alpha 0..1. Clamped to {@link FX.smokeAlphaMax}.
   * @param {number} [grey=1] 0..1 — light grey (1) to near-black (0). A lightly
   *   smoking car puffs pale; a burning one puffs soot.
   * @returns {boolean} `false` when the batch is full (and the puff dropped).
   */
  addSmoke(x, y, z, radius, alpha, grey = 1) {
    if (this._disposed) return false;
    return this._smoke.add(
      x, y, z, radius, alpha, FX.smokeAlphaMax,
      FX.smokeDark, FX.smokePale, grey,
    );
  }

  /**
   * One fire puff. Same quad, the other blend mode.
   *
   * @param {number} x @param {number} y @param {number} z WORLD centre.
   * @param {number} radius Metres.
   * @param {number} alpha 0..1. Clamped to {@link FX.fireAlphaMax} — the
   *   backstop on the additive sum, see {@link FX}.
   * @param {number} [heat=1] 0..1 — deep orange (0) to yellow-white core (1).
   * @returns {boolean} `false` when the batch is full.
   */
  addFire(x, y, z, radius, alpha, heat = 1) {
    if (this._disposed) return false;
    return this._fire.add(
      x, y, z, radius, alpha, FX.fireAlphaMax,
      FX.fireCool, FX.fireHot, heat,
    );
  }

  /**
   * Refreshes the billboard basis and uploads the frame. Call once per frame,
   * after the `add`s.
   */
  commit() {
    if (this._disposed) return;
    this._syncBasis();
    this._smoke.commit();
    this._fire.commit();
  }

  /**
   * @returns {{smoke: number, fire: number, maxSmoke: number, maxFire: number,
   *   draws: number, dropped: number}}
   */
  stats() {
    return {
      smoke: this._smoke.count,
      fire: this._fire.count,
      maxSmoke: this._smoke.max,
      maxFire: this._fire.max,
      draws: (this._smoke.count > 0 ? 1 : 0) + (this._fire.count > 0 ? 1 : 0),
      dropped: this._smoke.dropped + this._fire.dropped,
    };
  }

  /** Idempotent. */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object.removeFromParent();
    this._smoke.dispose();
    this._fire.dispose();
    this.texture.dispose();
  }

  /** @private Column 0 of the camera's world matrix is its RIGHT, column 1 its
   *  UP — both already normalised, which is what makes this two copies rather
   *  than a basis extraction. */
  _syncBasis() {
    const cam = this._camera;
    const e = cam?.matrixWorld?.elements;
    if (!e) return;
    this._uRight.value.set(e[0], e[1], e[2]);
    this._uUp.value.set(e[4], e[5], e[6]);
  }
}

/**
 * One instanced draw: the quad, the four per-instance buffers and the mesh.
 * Private — the two layers differ only in their material, their cap and their
 * colour ramp, and a second copy of this bookkeeping is how the smoke and the
 * fire drift out of step.
 */
class PuffLayer {
  /**
   * @param {number} max
   * @param {THREE.Material} mat
   * @param {string} name
   * @param {number} order `RENDER_ORDER.smoke` / `.fire`.
   */
  constructor(max, mat, name, order) {
    this.max = max | 0;
    /** @type {number} Instances written since the last `reset`. */
    this.count = 0;
    this.dropped = 0;
    this._warned = false;

    // An `InstancedBufferGeometry` + `Mesh`, deliberately NOT an
    // `InstancedMesh`: `NodeMaterial.setupPosition()` applies `instanceMatrix`
    // to `positionLocal`, so a world-space `positionNode` would be multiplied
    // by a per-instance matrix nobody wrote. Driving everything from instanced
    // ATTRIBUTES side-steps it and costs 16 floats/instance less. Same call
    // `render/Billboards.js` makes for its WebGL2 path.
    const geo = new THREE.InstancedBufferGeometry();
    geo.name = name;
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(QUAD_POS), 3));
    geo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array(QUAD_UV), 2));

    this._pos = new THREE.InstancedBufferAttribute(new Float32Array(this.max * 3), 3);
    this._size = new THREE.InstancedBufferAttribute(new Float32Array(this.max), 1);
    this._alpha = new THREE.InstancedBufferAttribute(new Float32Array(this.max), 1);
    this._tint = new THREE.InstancedBufferAttribute(new Float32Array(this.max * 3), 3);
    for (const a of [this._pos, this._size, this._alpha, this._tint]) {
      a.setUsage(THREE.DynamicDrawUsage);
    }
    geo.setAttribute('iPos', this._pos);
    geo.setAttribute('iSize', this._size);
    geo.setAttribute('iAlpha', this._alpha);
    geo.setAttribute('iTint', this._tint);
    // Slots past `instanceCount` are never submitted, so an untouched slot needs
    // no zeroing — the one bookkeeping saving of a rebuild over a pool.
    geo.instanceCount = 0;
    // The particles move every frame and follow the player's car; a correct
    // bounding sphere would have to be rebuilt every frame to save one draw.
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    this.geometry = geo;

    /** @type {THREE.Material} */
    this.material = mat;

    /** @type {THREE.Mesh} */
    this.object = new THREE.Mesh(geo, mat);
    this.object.name = name;
    this.object.renderOrder = order;
    this.object.frustumCulled = false;
    this.object.matrixAutoUpdate = false;
    this.object.updateMatrix();
    this.object.visible = false;
  }

  reset() {
    this.count = 0;
    this.dropped = 0;
  }

  /**
   * @param {number} x @param {number} y @param {number} z
   * @param {number} r @param {number} a
   * @param {number} aMax Per-layer alpha ceiling (the bloom backstop).
   * @param {ArrayLike<number>} c0 Colour at `t` = 0.
   * @param {ArrayLike<number>} c1 Colour at `t` = 1.
   * @param {number} t 0..1 ramp between them.
   * @returns {boolean}
   */
  add(x, y, z, r, a, aMax, c0, c1, t) {
    if (this.count >= this.max) {
      if (this.dropped++ === 0 && !this._warned) {
        this._warned = true;
        console.warn(`[VehicleFxBatch] ${this.object.name} full at ${this.max}; extras dropped`);
      }
      return false;
    }
    const i = this.count++;

    const p = i * 3;
    this._pos.array[p] = x;
    this._pos.array[p + 1] = y;
    this._pos.array[p + 2] = z;
    this._size.array[i] = r < FX.minRadius ? FX.minRadius : r > FX.maxRadius ? FX.maxRadius : r;
    this._alpha.array[i] = a < 0 ? 0 : a > aMax ? aMax : a;

    // The ramp is resolved on the CPU into a per-instance `iTint`, exactly as
    // `LightPools.js` and `MissionMarkers.js` resolve theirs — that is what lets
    // the materials stay two lines and share a shape.
    const k = t < 0 ? 0 : t > 1 ? 1 : t;
    this._tint.array[p] = c0[0] + (c1[0] - c0[0]) * k;
    this._tint.array[p + 1] = c0[1] + (c1[1] - c0[1]) * k;
    this._tint.array[p + 2] = c0[2] + (c1[2] - c0[2]) * k;
    return true;
  }

  commit() {
    this._pos.needsUpdate = true;
    this._size.needsUpdate = true;
    this._alpha.needsUpdate = true;
    this._tint.needsUpdate = true;
    this.geometry.instanceCount = this.count;
    this.object.visible = this.count > 0;
  }

  dispose() {
    this.object.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * The puff mask: `ShadowBlobs.js#makeFalloffTexture` at {@link FX}'s softness,
 * renamed so it is findable from a frame capture.
 *
 * ⚠ THERE IS NO SECOND GENERATOR HERE ON PURPOSE. The blob shadow's falloff is
 * already the right image — a soft radial smear in the ALPHA channel, built
 * arithmetically rather than through a canvas gradient (so this file constructs
 * in node), with a `softness` parameter that is exactly the "how big is the
 * core" knob a puff needs. A second hex→colour helper is a bug in this repo and
 * so is a second falloff.
 *
 * @returns {THREE.DataTexture}
 */
function makePuffTexture() {
  const tex = makeFalloffTexture(FX.puffSize, FX.puffSoftness);
  tex.name = 'puffFalloff';
  return tex;
}
